import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ChevronLeft, ChevronRight, MapPin, Clock,
  CheckCircle, Wrench, Phone, Users, Mic, MicOff, ChevronDown, ChevronUp,
  AlertTriangle, Settings, Zap, ClipboardList, Loader2, MessageSquare,
} from 'lucide-react';
import { DiscussionsFicheTab } from '../../components/DiscussionsFicheTab';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import * as api from '../../api';
import { TerrainProfileButton } from './TerrainComptages';

// ─── Utilitaires ──────────────────────────────────────────────────────────────

function getLundi(d) {
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1 - day);
  const lundi = new Date(d);
  lundi.setDate(d.getDate() + diff);
  return lundi;
}

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const j = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${j}`;
}

function fmtDate(iso) {
  return new Date(iso + 'T12:00:00').toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long',
  });
}

function fmtH(h) {
  if (!h && h !== 0) return '—';
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${hh}h${mm > 0 ? String(mm).padStart(2, '0') : ''}`;
}

/** Calcule la durée en heures entre deux strings "HH:MM". Retourne null si invalide. */
function calcDuree(ha, hd) {
  if (!ha || !hd) return null;
  const [hh, hm] = ha.split(':').map(Number);
  const [dh, dm] = hd.split(':').map(Number);
  const diff = (dh * 60 + dm) - (hh * 60 + hm);
  if (diff <= 0) return null;
  return Math.round((diff / 60) * 4) / 4; // arrondi au 0.25h
}

function fmtDuree(h) {
  if (h == null) return null;
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return mm > 0 ? `${hh}h${String(mm).padStart(2, '0')}` : `${hh}h`;
}

// Badge couleur par type d'OR
const TYPE_OR_CONFIG = {
  entretien:        { label: 'Entretien',  bg: '#DBEAFE', color: '#1D4ED8', Icon: Settings  },
  panne:            { label: 'Panne',      bg: '#FEE2E2', color: '#B91C1C', Icon: AlertTriangle },
  urgent:           { label: 'Urgent',     bg: '#FEF9C3', color: '#92400E', Icon: Zap       },
  reparation:       { label: 'Réparation', bg: '#F3F4F6', color: '#374151', Icon: Wrench    },
};
function TypeORBadge({ type }) {
  if (!type) return null;
  const cfg = TYPE_OR_CONFIG[type] || { label: type, bg: '#F3F4F6', color: '#374151', Icon: Wrench };
  const { Icon } = cfg;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ background: cfg.bg, color: cfg.color }}>
      <Icon size={10} />
      {cfg.label}
    </span>
  );
}

// ─── Ligne de pointage (par article_mo_id) ─────────────────────────────────

function LignePointage({ row, timeState, onTimeChange }) {
  const label = row.article_designation || 'Intervention';
  const duree = calcDuree(timeState.heure_arrivee, timeState.heure_depart);

  return (
    <div className="rounded-xl p-3" style={{ background: '#F8FAFC', border: '1px solid #E2E8F0' }}>
      {/* Label de la ligne */}
      <div className="mb-2">
        <p className="text-xs font-bold uppercase tracking-wide" style={{ color: '#374151' }}>
          {label}
        </p>
        {row.duree_affectee != null && (
          <p className="text-xs mt-0.5" style={{ color: '#94A3B8' }}>
            Durée allouée : <strong style={{ color: '#64748B' }}>{fmtH(row.duree_affectee)}</strong>
          </p>
        )}
      </div>

      {/* Inputs temps - 3 colonnes : Début | Fin | Temps */}
      <div className="flex gap-2 items-end">
        <div style={{ flex: '2 1 0', minWidth: 0 }}>
          <label className="block text-xs mb-1" style={{ color: '#64748B' }}>Début</label>
          <input
            type="time"
            value={timeState.heure_arrivee}
            onChange={e => onTimeChange('heure_arrivee', e.target.value)}
            data-testid={`terrain-arrivee-${row.ot_id}`}
            className="w-full rounded-lg border px-2 py-2 outline-none focus:ring-2 focus:ring-blue-200"
            style={{ borderColor: '#D1D5DB', fontSize: 15 }}
          />
        </div>
        <div style={{ flex: '2 1 0', minWidth: 0 }}>
          <label className="block text-xs mb-1" style={{ color: '#64748B' }}>Fin</label>
          <input
            type="time"
            value={timeState.heure_depart}
            onChange={e => onTimeChange('heure_depart', e.target.value)}
            data-testid={`terrain-depart-${row.ot_id}`}
            className="w-full rounded-lg border px-2 py-2 outline-none focus:ring-2 focus:ring-blue-200"
            style={{ borderColor: '#D1D5DB', fontSize: 15 }}
          />
        </div>
        <div style={{ flex: '1.4 1 0', minWidth: 0 }}>
          <label className="block text-xs mb-1" style={{ color: '#64748B' }}>Temps</label>
          {duree != null ? (
            <div className="flex items-center justify-center gap-1 rounded-lg px-1 py-2"
              style={{ background: '#DCFCE7', height: 40 }}>
              <Clock size={12} style={{ color: '#15803D' }} />
              <span className="text-sm font-bold" style={{ color: '#15803D' }}>{fmtDuree(duree)}</span>
            </div>
          ) : (
            <div className="rounded-lg flex items-center justify-center"
              style={{ background: '#F3F4F6', border: '1px dashed #D1D5DB', height: 40 }}>
              <span className="text-xs" style={{ color: '#9CA3AF' }}>—</span>
            </div>
          )}
        </div>
      </div>

      {/* Indication si une seule heure saisie */}
      {(!timeState.heure_arrivee || !timeState.heure_depart) && (timeState.heure_arrivee || timeState.heure_depart) && (
        <p className="text-xs mt-1.5" style={{ color: '#9CA3AF' }}>
          Saisir début et fin pour calculer le temps
        </p>
      )}
    </div>
  );
}

// ─── Carte d'intervention (groupée par ordre_id) ───────────────────────────

function AffectationCard({ rows, onSaved }) {
  const ref = rows[0]; // données OR issues de la 1ère ligne
  const [open, setOpen] = useState(false);
  const [showContact,  setShowContact]  = useState(false);
  const [showTravaux,  setShowTravaux]  = useState(false);
  const [showTravDem,  setShowTravDem]  = useState(false);
  const [showTravPrev, setShowTravPrev] = useState(false);
  const [showDiscussions, setShowDiscussions] = useState(false);
  const [savingPointage, setSavingPointage] = useState(false);
  const [savingNote,     setSavingNote]     = useState(false);
  const [creatingFiche, setCreatingFiche] = useState(false);
  const [listening, setListening] = useState(false);
  const recogRef = useRef(null);
  const noteRef  = useRef(null);

  // État des temps par ot_id
  const [timeMap, setTimeMap] = useState(() =>
    Object.fromEntries(rows.map(r => [r.ot_id, {
      heure_arrivee: r.heure_arrivee || '',
      heure_depart:  r.heure_depart  || '',
    }]))
  );

  // Note partagée (initialise depuis la 1ère ligne non vide)
  const [note, setNote] = useState(() =>
    rows.map(r => r.note).find(n => n) || ''
  );

  // Auto-resize du textarea Note
  const handleNoteChange = (e) => {
    setNote(e.target.value);
    const el = noteRef.current;
    if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; }
  };

  // Initialise la hauteur à l'ouverture de la carte (note existante)
  useEffect(() => {
    if (!open) return;
    const el = noteRef.current;
    if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; }
  }, [open]);

  const matNom = [ref.mat_marque, ref.mat_modele].filter(Boolean).join(' ') || null;
  const matId  = [ref.mat_serie, ref.mat_immatriculation].filter(Boolean).join(' · ') || null;
  const isExterieur = ref.lieu_intervention === 'exterieur';
  const anyPointe = rows.some(r => r.duree_reelle != null);

  // Totaux pour affichage droite
  const totalAlloue = rows.reduce((s, r) => s + (r.duree_affectee || 0), 0);
  const totalPointe = rows.filter(r => r.duree_reelle != null)
                          .reduce((s, r) => s + (r.duree_reelle || 0), 0);

  // Contacts depuis contacts_list (même valeur sur toutes les lignes du même OR)
  const contactsList = ref.contacts_list || [];

  const handleTimeChange = (ot_id, field, value) => {
    setTimeMap(prev => ({ ...prev, [ot_id]: { ...prev[ot_id], [field]: value } }));
  };

  const handleCreerFiche = async () => {
    setCreatingFiche(true);
    try {
      const res = await api.creerFicheIntervention(ref.ordre_id);
      const { url } = res.data;
      window.open(url, '_blank');
    } catch {
      toast.error("Erreur lors de la création de la fiche");
    } finally {
      setCreatingFiche(false);
    }
  };

  const handleSavePointage = async () => {
    setSavingPointage(true);
    const updates = [];
    try {
      for (const row of rows) {
        const { heure_arrivee, heure_depart } = timeMap[row.ot_id];
        const duree_reelle = calcDuree(heure_arrivee, heure_depart);
        const payload = {
          ...(heure_arrivee ? { heure_arrivee } : {}),
          ...(heure_depart  ? { heure_depart  } : {}),
          ...(duree_reelle != null ? { duree_reelle } : {}),
        };
        if (Object.keys(payload).length) {
          await api.updateORTechnicien(row.ot_id, payload);
          updates.push({ ot_id: row.ot_id, heure_arrivee, heure_depart, duree_reelle });
        }
      }
      toast.success('Pointage enregistré');
      onSaved(updates);
    } catch {
      toast.error('Erreur lors du pointage');
    } finally {
      setSavingPointage(false);
    }
  };

  const handleSaveNote = async () => {
    const text = note.trim();
    if (!text) return;
    setSavingNote(true);
    try {
      const today = new Date();
      const dateStr = today.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
      const noms = [...new Set(rows.map(r => r.employe_nom).filter(Boolean))].join(', ');
      const entete = `**${dateStr}${noms ? ` — ${noms}` : ''}**`;
      const bloc   = `${entete}\n${text}`;
      await api.appendTravauxOR(ref.ordre_id, bloc);
      setNote('');
      if (noteRef.current) {
        noteRef.current.style.height = 'auto';
        noteRef.current.style.height = noteRef.current.scrollHeight + 'px';
      }
      toast.success('Note enregistrée dans les travaux effectués');
    } catch {
      toast.error('Erreur lors de l\'enregistrement de la note');
    } finally {
      setSavingNote(false);
    }
  };

  // ── Enregistrement vocal ──
  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error('Reconnaissance vocale non supportée sur ce navigateur'); return; }
    const rec = new SR();
    rec.lang = 'fr-FR';
    rec.interimResults = false;
    rec.onresult = (e) => {
      const t = e.results[0][0].transcript;
      setNote(prev => prev ? `${prev} ${t}` : t);
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => { setListening(false); toast.error('Erreur micro'); };
    rec.start();
    recogRef.current = rec;
    setListening(true);
  };

  const stopVoice = () => {
    recogRef.current?.stop();
    setListening(false);
  };

  return (
    <div
      data-testid={`terrain-card-${ref.ordre_id}`}
      className="rounded-2xl overflow-hidden"
      style={{
        background: 'white',
        border: anyPointe ? '2px solid #86EFAC' : '1.5px solid #E5E7EB',
        boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
      }}
    >
      {/* ── En-tête cliquable ── */}
      <button
        className="w-full text-left p-4"
        onClick={() => setOpen(o => !o)}
        data-testid={`terrain-header-${ref.ordre_id}`}
      >
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">

            {/* N° OR + Type OR */}
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              <span className="text-xs font-bold tracking-wide" style={{ color: 'var(--primary-solid, #1A3A6E)' }}>
                {ref.or_numero}
              </span>
              <TypeORBadge type={ref.type_or} />
            </div>

            {/* Client (gras, mis en avant) */}
            {ref.client_nom && (
              <p className="text-base font-bold leading-tight mb-1" style={{ color: '#111827',  }}>
                {ref.client_nom}
              </p>
            )}

            {/* Matériel */}
            {matNom && (
              <p className="text-sm font-medium" style={{ color: '#374151' }}>
                {matNom}
              </p>
            )}

            {/* N° série / Immatriculation */}
            {matId && (
              <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>
                {matId}
              </p>
            )}

            {/* Titre OR */}
            {ref.or_titre && (
              <p className="text-sm mt-1 italic" style={{ color: '#4B5563' }}>
                {ref.or_titre}
              </p>
            )}

            {/* Lieu extérieur */}
            {isExterieur && (
              <div className="flex items-center gap-1 mt-1.5">
                <MapPin size={12} style={{ color: '#EF4444' }} />
                <span className="text-xs font-medium" style={{ color: '#EF4444' }}>
                  Extérieur{ref.adresse_intervention ? ` — ${ref.adresse_intervention}` : ''}
                </span>
              </div>
            )}
          </div>

          {/* Droite : alloué / pointé + flèche */}
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            {open
              ? <ChevronUp size={18} style={{ color: '#9CA3AF' }} />
              : <ChevronDown size={18} style={{ color: '#9CA3AF' }} />
            }
            <div className="flex items-center gap-1 text-xs mt-0.5" style={{ color: '#6B7280' }}>
              <Clock size={11} />
              <span>Allouées <strong>{fmtH(totalAlloue)}</strong></span>
            </div>
            {anyPointe && (
              <div className="flex items-center gap-1 text-xs font-semibold" style={{ color: '#16A34A' }}>
                <CheckCircle size={11} />
                <span>Pointées {fmtH(totalPointe)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Résumé des temps si pointé (fermé) */}
        {anyPointe && !open && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {rows.filter(r => r.duree_reelle != null).map(r => (
              <span key={r.ot_id} className="text-xs px-2 py-0.5 rounded-full font-semibold"
                style={{ background: '#DCFCE7', color: '#15803D' }}>
                {r.article_designation || 'Intervention'} · {fmtH(r.duree_reelle)}
              </span>
            ))}
          </div>
        )}
      </button>

      {/* ── Corps du formulaire ── */}
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t" style={{ borderColor: '#F3F4F6' }}>

          {/* Contacts (affiché au clic) */}
          {/* ── Contacts ─────────────────────────────────────────── */}
          {contactsList.length > 0 && (
            <div className="pt-2">
              <button
                onClick={() => setShowContact(c => !c)}
                className="flex items-center gap-2 text-sm font-semibold"
                style={{ color: '#1D4ED8' }}
                data-testid={`terrain-contact-btn-${ref.ordre_id}`}
              >
                <Users size={14} />
                {showContact ? 'Masquer les contacts' : `Afficher les contacts (${contactsList.length})`}
              </button>
              {showContact && (
                <div className="mt-2 space-y-2">
                  {contactsList.map((c, i) => (
                    <div key={i}
                      className="rounded-xl p-3 flex items-center justify-between"
                      style={{ background: '#EFF6FF', border: '1px solid #BFDBFE' }}>
                      <div>
                        <p className="text-sm font-semibold" style={{ color: '#1E3A8A' }}>
                          {c.nom?.trim() || '—'}
                        </p>
                        {c.tel && (
                          <p className="text-sm" style={{ color: '#1D4ED8' }}>{c.tel}</p>
                        )}
                      </div>
                      {c.tel && (
                        <a
                          href={`tel:${c.tel}`}
                          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ml-2"
                          style={{ background: '#1D4ED8' }}
                          data-testid={`terrain-call-${ref.ordre_id}-${i}`}
                        >
                          <Phone size={18} color="white" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Travaux demandés ──────────────────────────────────── */}
          {ref.travaux_demandes && (
            <div className="pt-2">
              <button
                onClick={() => setShowTravDem(t => !t)}
                className="flex items-center gap-2 text-sm font-semibold"
                style={{ color: '#1D4ED8' }}
                data-testid={`terrain-trav-dem-btn-${ref.ordre_id}`}
              >
                <ClipboardList size={14} />
                {showTravDem ? 'Masquer les travaux demandés' : 'Afficher les travaux demandés'}
              </button>
              {showTravDem && (
                <div className="mt-2 rounded-xl p-3"
                  style={{ background: '#EFF6FF', border: '1px solid #BFDBFE' }}>
                  {ref.travaux_demandes.split('\n').map((line, i) => {
                    const bold = line.match(/^\*\*(.+)\*\*$/);
                    return (
                      <React.Fragment key={i}>
                        {bold
                          ? <strong style={{ color: '#1E3A8A', display: 'block' }}>{bold[1]}</strong>
                          : <span style={{ color: '#1E40AF', fontSize: '0.85rem', display: 'block' }}>{line}</span>
                        }
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Travaux effectués ──────────────────────────────────── */}
          {ref.travaux_effectues && (
            <div className="pt-2">
              <button
                onClick={() => setShowTravaux(t => !t)}
                className="flex items-center gap-2 text-sm font-semibold"
                style={{ color: '#059669' }}
                data-testid={`terrain-travaux-btn-${ref.ordre_id}`}
              >
                <ClipboardList size={14} />
                {showTravaux ? 'Masquer les travaux effectués' : 'Afficher les travaux effectués'}
              </button>
              {showTravaux && (
                <div className="mt-2 rounded-xl p-3"
                  style={{ background: '#F0FDF4', border: '1px solid #BBF7D0' }}>
                  {ref.travaux_effectues.split('\n').map((line, i) => {
                    const bold = line.match(/^\*\*(.+)\*\*$/);
                    return (
                      <React.Fragment key={i}>
                        {bold
                          ? <strong style={{ color: '#065F46', display: 'block' }}>{bold[1]}</strong>
                          : <span style={{ color: '#134E4A', fontSize: '0.85rem', display: 'block' }}>{line}</span>
                        }
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Travaux à prévoir ──────────────────────────────────── */}
          {ref.travaux_a_prevoir && (
            <div className="pt-2">
              <button
                onClick={() => setShowTravPrev(t => !t)}
                className="flex items-center gap-2 text-sm font-semibold"
                style={{ color: '#B45309' }}
                data-testid={`terrain-trav-prev-btn-${ref.ordre_id}`}
              >
                <ClipboardList size={14} />
                {showTravPrev ? 'Masquer les travaux à prévoir' : 'Afficher les travaux à prévoir'}
              </button>
              {showTravPrev && (
                <div className="mt-2 rounded-xl p-3"
                  style={{ background: '#FFFBEB', border: '1px solid #FDE68A' }}>
                  {ref.travaux_a_prevoir.split('\n').map((line, i) => {
                    const bold = line.match(/^\*\*(.+)\*\*$/);
                    return (
                      <React.Fragment key={i}>
                        {bold
                          ? <strong style={{ color: '#92400E', display: 'block' }}>{bold[1]}</strong>
                          : <span style={{ color: '#78350F', fontSize: '0.85rem', display: 'block' }}>{line}</span>
                        }
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Discussions ──────────────────────────────────── */}
          <div className="pt-2">
            <button
              onClick={() => setShowDiscussions(d => !d)}
              className="flex items-center gap-2 text-sm font-semibold"
              style={{ color: '#6D28D9' }}
              data-testid={`terrain-discussions-btn-${ref.ordre_id}`}
            >
              <MessageSquare size={14} />
              {showDiscussions ? 'Masquer les discussions' : 'Afficher les discussions'}
            </button>
            {showDiscussions && (
              <div className="mt-2 rounded-xl overflow-hidden"
                style={{ border: '1px solid #DDD6FE', background: 'white', minHeight: 220, display: 'flex', flexDirection: 'column' }}>
                <DiscussionsFicheTab
                  record_type="atelier"
                  record_id={ref.ordre_id}
                  record_label={ref.or_numero}
                />
              </div>
            )}
          </div>

          {/* Lignes de pointage */}
          <div className="space-y-2 pt-1">
            {rows.map(row => (
              <LignePointage
                key={row.ot_id}
                row={row}
                timeState={timeMap[row.ot_id]}
                onTimeChange={(field, value) => handleTimeChange(row.ot_id, field, value)}
              />
            ))}
          </div>

          {/* Bouton enregistrer le pointage */}
          <button
            onClick={handleSavePointage}
            disabled={savingPointage}
            data-testid={`terrain-save-pointage-${ref.ordre_id}`}
            className="w-full py-3 rounded-xl text-sm font-bold transition-all"
            style={{
              background: savingPointage ? '#9CA3AF' : 'var(--primary-solid, #1A3A6E)',
              color: 'white', fontSize: 15,
            }}
          >
            {savingPointage ? 'Enregistrement...' : '✓ Enregistrer le pointage'}
          </button>

          {/* Note + Micro */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#6B7280' }}>
                Note
              </label>
              <button
                type="button"
                onClick={listening ? stopVoice : startVoice}
                data-testid={`terrain-mic-${ref.ordre_id}`}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                style={{
                  background: listening ? '#FEE2E2' : '#F1F5F9',
                  color:      listening ? '#B91C1C' : '#475569',
                  border:     `1px solid ${listening ? '#FECACA' : '#E2E8F0'}`,
                }}
              >
                {listening ? <MicOff size={13} /> : <Mic size={13} />}
                {listening ? 'Arrêter' : 'Dicter'}
              </button>
            </div>
            <textarea
              ref={noteRef}
              value={note}
              onChange={handleNoteChange}
              placeholder="Observations, travaux effectués, remarques..."
              rows={3}
              data-testid={`terrain-note-${ref.ordre_id}`}
              className="w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-200"
              style={{ borderColor: '#D1D5DB', fontSize: 16, resize: 'none', minHeight: 72, overflow: 'hidden' }}
            />
          </div>

          {/* Bouton enregistrer la note */}
          <button
            onClick={handleSaveNote}
            disabled={savingNote || !note.trim()}
            data-testid={`terrain-save-note-${ref.ordre_id}`}
            className="w-full py-3 rounded-xl text-sm font-bold transition-all"
            style={{
              background: savingNote ? '#9CA3AF' : note.trim() ? '#059669' : '#D1FAE5',
              color: note.trim() ? 'white' : '#6B7280',
              fontSize: 14,
            }}
          >
            {savingNote ? 'Enregistrement...' : '✓ Enregistrer la note'}
          </button>

          {/* Bouton fiche d'intervention */}
          <button
            onClick={handleCreerFiche}
            disabled={creatingFiche}
            data-testid={`terrain-fiche-${ref.ordre_id}`}
            className="w-full py-3 rounded-xl text-sm font-semibold transition-all"
            style={{
              background: creatingFiche ? '#EDE9FE' : '#FAF5FF',
              color: creatingFiche ? '#9C81E8' : '#7C3AED',
              border: '1.5px solid #DDD6FE',
              fontSize: 14, cursor: creatingFiche ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}
          >
            {creatingFiche
              ? <><Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Création…</>
              : <><ClipboardList size={14} /> Créer fiche d'intervention</>
            }
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────

export default function TerrainAtelier() {
  const navigate  = useNavigate();
  const { user }  = useAuth();
  const [semaine, setSemaine] = useState(isoDate(getLundi(new Date())));
  const [loading, setLoading] = useState(false);
  const [affs,    setAffs]    = useState([]);

  const load = useCallback(async (sem) => {
    setLoading(true);
    try {
      const r = await api.getTerrainMesOrs(sem);
      setAffs(r.data.affectations || []);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(msg || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(semaine); }, [semaine, load]);

  const prevSemaine = () => {
    const d = new Date(semaine + 'T12:00:00');
    d.setDate(d.getDate() - 7);
    setSemaine(isoDate(d));
  };

  const nextSemaine = () => {
    const d = new Date(semaine + 'T12:00:00');
    d.setDate(d.getDate() + 7);
    setSemaine(isoDate(d));
  };

  const handleSaved = (updates) => {
    setAffs(prev => prev.map(a => {
      const upd = updates.find(u => u.ot_id === a.ot_id);
      return upd ? { ...a, ...upd } : a;
    }));
  };

  // ── Grouper par jour → puis par ordre_id ──────────────────────────────────
  const parJour = {};
  affs.forEach(a => {
    const jour = a.date_affectation;
    if (!parJour[jour]) parJour[jour] = {};
    if (!parJour[jour][a.ordre_id]) parJour[jour][a.ordre_id] = [];
    parJour[jour][a.ordre_id].push(a);
  });
  const joursAvecAff = Object.keys(parJour).sort();

  // Compte total d'ORs distincts affichés
  const totalOrs = Object.values(parJour).reduce((s, j) => s + Object.keys(j).length, 0);

  // Label semaine
  const lundiFmt = new Date(semaine + 'T12:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  const vendredi = new Date(semaine + 'T12:00:00');
  vendredi.setDate(vendredi.getDate() + 4);
  const vendrediFmt = vendredi.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  const annee = new Date(semaine + 'T12:00:00').getFullYear();

  const firstName = user?.name?.split(' ')[0] || 'Technicien';

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#F1F5F9',  }}>

      {/* Header */}
      <div className="sticky top-0 z-10 px-4 pt-4 pb-3" style={{ background: '#1A3A6E' }}>
        <div className="flex items-center gap-3 mb-3">
          <button
            onClick={() => navigate('/terrain')}
            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(255,255,255,0.15)' }}
            data-testid="terrain-back-btn"
          >
            <ArrowLeft size={18} color="white" />
          </button>
          <div className="flex-1">
            <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.6)' }}>
              Mes interventions
            </p>
            <p className="text-sm font-bold" style={{ color: 'white' }}>{firstName}</p>
          </div>
          <TerrainProfileButton />
        </div>

        {/* Navigation semaine */}
        <div className="flex items-center justify-between rounded-xl px-3 py-2"
          style={{ background: 'rgba(255,255,255,0.12)' }}>
          <button
            onClick={prevSemaine}
            data-testid="terrain-prev-semaine"
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: 'rgba(255,255,255,0.15)' }}
          >
            <ChevronLeft size={18} color="white" />
          </button>
          <div className="text-center">
            <p className="text-xs font-semibold" style={{ color: 'rgba(255,255,255,0.7)' }}>
              {lundiFmt} — {vendrediFmt} {annee}
            </p>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>
              {totalOrs} intervention{totalOrs !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={nextSemaine}
            data-testid="terrain-next-semaine"
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: 'rgba(255,255,255,0.15)' }}
          >
            <ChevronRight size={18} color="white" />
          </button>
        </div>
      </div>

      {/* Contenu */}
      <div className="flex-1 px-4 py-5 space-y-6 pb-10">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full"
              style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
          </div>
        ) : !user?.employe_id ? (
          <div className="flex flex-col items-center justify-center py-16 text-center px-6">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
              style={{ background: '#FEF3C7' }}>
              <Wrench size={28} style={{ color: '#92400E' }} />
            </div>
            <p className="text-base font-bold mb-2" style={{ color: '#111827' }}>
              Compte non configuré
            </p>
            <p className="text-sm" style={{ color: '#6B7280' }}>
              Votre compte n'est pas encore lié à un employé. Contactez votre administrateur Bedee.
            </p>
          </div>
        ) : joursAvecAff.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
              style={{ background: '#F3F4F6' }}>
              <Clock size={28} style={{ color: '#9CA3AF' }} />
            </div>
            <p className="text-base font-bold mb-1" style={{ color: '#374151' }}>
              Aucune intervention cette semaine
            </p>
            <p className="text-sm" style={{ color: '#9CA3AF' }}>
              Naviguez vers une autre semaine ou contactez votre gestionnaire.
            </p>
          </div>
        ) : (
          joursAvecAff.map(jour => (
            <div key={jour}>
              <p className="text-xs font-bold uppercase tracking-widest mb-3 capitalize"
                style={{ color: '#64748B' }}>
                {fmtDate(jour)}
  
              </p>
              <div className="space-y-3">
                {Object.entries(parJour[jour]).map(([ordreId, rows]) => (
                  <AffectationCard
                    key={`${ordreId}-${jour}`}
                    rows={rows}
                    onSaved={handleSaved}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
